import {
  createMatterNoteCommentV1,
  deleteMatterNoteCommentV1,
  updateMatterNoteCommentV1,
} from "@/lib/data/requests";
import {
  getAnnotationQueryKey,
  invalidateAnnotationQueries,
} from "@/lib/data/react-query/annotation-query-utils";
import { type APIError, type APIResponse, type Matter } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import dayjs from "dayjs";
import toast from "react-hot-toast";

export const useCreateMatterNoteComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Parameters<typeof createMatterNoteCommentV1>[0]) =>
      createMatterNoteCommentV1(data),
    onMutate: (data) => {
      if (data.massTortId) {
        return;
      }
      const queryKey = getAnnotationQueryKey(data);
      const previousData =
        queryClient.getQueryData<APIResponse<Matter>>(queryKey);
      const timestamp = dayjs().toISOString().replace("Z", "");
      const optimisticId = crypto.randomUUID();

      queryClient.setQueryData(
        queryKey,
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;

          const optimisticComment = {
            emailId: null,
            expenseId: null,
            fileId: null,
            incidentReportId: null,
            insuranceId: null,
            lienId: null,
            litigationDateId: null,
            lostWagesId: null,
            medicalTreatmentAppointmentId: null,
            medicalTreatmentBalanceConfirmationId: null,
            medicalTreatmentBillId: null,
            medicalTreatmentId: null,
            medicalTreatmentPlanId: null,
            medicalTreatmentRecordId: null,
            propertyDamageId: null,
            settlementCounterId: null,
            settlementDemandId: null,
            settlementOfferId: null,
            shareFolderId: null,
            signatureRequestId: null,
            statuteOfLimitationsId: null,
            trustCheckId: null,
            vehicleId: null,
            id: optimisticId,
            message: data.message,
            noteId: data.noteId,
            matterId: data.matterId,
            massTortId: null,
            firmId: oldData.data.firmId,
            userId: data.userId,
            draft: false,
            publishedDate: timestamp,
            dateCreated: timestamp,
            dateModified: timestamp,
          };

          return {
            data: {
              ...oldData.data,
              noteComments: [...oldData.data.noteComments, optimisticComment],
            },
          };
        },
      );

      return {
        previousData,
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          getAnnotationQueryKey(variables),
          context.previousData,
        );
      }

      toastAPIErrorMessage(error, "Error creating matter note comment");
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateAnnotationQueries(queryClient, variables);
    },
    onSuccess: () => {
      toast.success("Matter note comment created successfully");
    },
  });
};

export const useUpdateMatterNoteComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterNoteCommentV1>[0]) =>
      updateMatterNoteCommentV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error updating matter note comment");
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateAnnotationQueries(queryClient, variables);
    },
    onSuccess: () => {
      toast.success("Matter note comment updated successfully");
    },
  });
};

export const useDeleteMatterNoteComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatterNoteCommentV1>[0]) =>
      deleteMatterNoteCommentV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error deleting matter note comment");
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateAnnotationQueries(queryClient, variables);
    },
    onSuccess: () => {
      toast.success("Matter note comment deleted successfully");
    },
  });
};
